// VRT "Complete the look" bundle strip. Wires checkbox selection, per-item
// variant selects, and the current product's live variant (mirrored from the
// main <vrt-product-form> buy box) into a running "N items selected · Total"
// footer, then adds every checked line in a single atomic /cart/add.js
// request and opens the existing <vrt-cart> drawer via its public API.
//
// Per-item prices stay Liquid-pre-rendered strings (swap-only, never
// formatted here); only the combinatorial running total is computed
// client-side, using Horizon's own money-formatting helper — the same
// real-time exception assets/facets.js already relies on.
import { formatMoney } from '@theme/money-formatting';

class VrtShopTheLook extends HTMLElement {
  connectedCallback() {
    this.itemsEl = this.querySelector('[data-vrt-look-items]');
    this.footerEl = this.querySelector('[data-vrt-look-footer]');
    this.countEl = this.querySelector('[data-vrt-look-count]');
    this.totalEl = this.querySelector('[data-vrt-look-total]');
    this.totalWrapEl = this.querySelector('[data-vrt-look-total-wrap]');
    this.errorEl = this.querySelector('[data-vrt-look-error]');
    this.addBtn = this.querySelector('[data-vrt-look-add]');

    this.addEventListener('change', (e) => {
      if (e.target.matches('[data-vrt-look-check]')) this.updateFooter();
      else if (e.target.matches('[data-vrt-look-opt]')) this.onOptionChange(e.target);
    });
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-vrt-look-add]')) this.addSelected();
    });

    // The main buy box's option radios live outside this element — listen at
    // document level so the "This item" tile stays in sync when the shopper
    // changes size/colour up in the PDP form.
    this._onDocChange = (e) => {
      if (!e.target.matches('[data-vrt-opt]')) return;
      this.syncCurrent();
      this.applyMainSizeToAll();
    };
    document.addEventListener('change', this._onDocChange);

    this.initItems();

    if (this.dataset.mode === 'recs') {
      this.observer = new IntersectionObserver(
        (entries, obs) => {
          if (!entries[0]?.isIntersecting) return;
          obs.disconnect();
          this.load();
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      this.observer.observe(this);
    }
  }

  disconnectedCallback() {
    document.removeEventListener('change', this._onDocChange);
    this.observer?.disconnect();
  }

  initItems() {
    this.items = Array.from(this.querySelectorAll('[data-vrt-look-item]')).map((el) => {
      const dataEl = el.querySelector('[data-vrt-look-item-data]');
      let data = { variants: [] };
      try {
        data = JSON.parse(dataEl.textContent);
      } catch (e) {
        /* leave empty */
      }
      const item = {
        el,
        data,
        variant: null,
        current: el.hasAttribute('data-vrt-look-current'),
        tagMto: el.dataset.vrtLookTagMto === 'true',
      };
      if (item.current) {
        this.currentItem = item;
        this.syncCurrent();
      } else {
        this.applyMainSize(item);
        this.resolveItem(item);
      }
      return item;
    });
    this.updateFooter();
  }

  // --- "This item" tile: mirror the live buy-box selection ---
  syncCurrent() {
    if (!this.currentItem) return;
    const btn = document.querySelector('vrt-product-form [data-vrt-quick-add]');
    const id = btn?.dataset.variantId;
    const variants = this.currentItem.data.variants || [];
    const variant = (id && variants.find((v) => String(v.id) === String(id))) || variants[0] || null;
    this.currentItem.variant = variant;
    this.updateItem(this.currentItem, {
      labelEl: this.currentItem.el.querySelector('[data-vrt-look-current-label]'),
    });
    this.updateFooter();
  }

  // --- per-item variant selects ---
  onOptionChange(select) {
    const el = select.closest('[data-vrt-look-item]');
    const item = (this.items || []).find((i) => i.el === el);
    if (!item) return;
    // Only a real shopper edit fires a 'change' event — programmatic
    // applyMainSize() sets .value directly, so this flag never fires from
    // our own default-matching and only marks a genuine manual override.
    if (select.dataset.optionName === 'size') item.sizeTouched = true;
    this.resolveItem(item);
    this.updateFooter();
  }

  // --- default each item's own Size select to the main product's chosen
  // size, when that size exists among the item's options and still
  // resolves to a real, in-stock-or-not variant combo (never override a
  // size the shopper has manually picked on this item already) ---
  getMainSize() {
    const input = document.querySelector('vrt-product-form [data-vrt-opt][data-option-name="size"]:checked');
    return input ? input.value : null;
  }

  applyMainSize(item) {
    if (item.current || item.sizeTouched) return;
    const mainSize = this.getMainSize();
    if (!mainSize) return;
    const sizeSelect = item.el.querySelector('[data-vrt-look-opt][data-option-name="size"]');
    if (!sizeSelect || sizeSelect.value === mainSize) return;
    const hasSize = Array.from(sizeSelect.options).some((o) => o.value === mainSize);
    if (!hasSize) return;

    const previous = sizeSelect.value;
    sizeSelect.value = mainSize;
    const selects = Array.from(item.el.querySelectorAll('[data-vrt-look-opt]'));
    const sel = [];
    selects.forEach((s) => {
      sel[Number(s.dataset.position) - 1] = s.value;
    });
    const variants = item.data.variants || [];
    const matches = variants.some(
      (v) => v.options.length === sel.length && v.options.every((o, i) => o === sel[i])
    );
    if (!matches) sizeSelect.value = previous; // no real variant at this size — keep the item's own default
  }

  applyMainSizeToAll() {
    (this.items || []).forEach((item) => {
      if (item.current) return;
      this.applyMainSize(item);
      this.resolveItem(item);
    });
    this.updateFooter();
  }

  resolveItem(item) {
    const selects = Array.from(item.el.querySelectorAll('[data-vrt-look-opt]'));
    const variants = item.data.variants || [];
    let variant;
    if (selects.length === 0) {
      variant = variants[0] || null;
    } else {
      const sel = [];
      selects.forEach((s) => {
        sel[Number(s.dataset.position) - 1] = s.value;
      });
      variant =
        variants.find(
          (v) => v.options.length === sel.length && v.options.every((o, i) => o === sel[i])
        ) || null;
    }
    item.variant = variant;
    this.updateItem(item);
  }

  updateItem(item, opts = {}) {
    const { el, variant } = item;
    const checkbox = el.querySelector('[data-vrt-look-check]');
    const priceEl = el.querySelector('[data-vrt-look-price]');
    const compareEl = el.querySelector('[data-vrt-look-compare]');
    const statusEl = el.querySelector('[data-vrt-look-status]');
    const available = !!(variant && variant.available);

    if (priceEl && variant) priceEl.textContent = variant.price;
    if (compareEl) {
      if (variant && variant.compare) {
        compareEl.textContent = variant.compare;
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }
    if (opts.labelEl && variant) opts.labelEl.textContent = variant.label;

    if (checkbox) {
      if (!available) {
        // Only capture on the enabled→disabled edge, so repeated "still
        // unavailable" calls don't stomp the shopper's real preference with
        // the false we just forced.
        if (!checkbox.disabled) item.wasChecked = checkbox.checked;
        checkbox.checked = false;
        checkbox.disabled = true;
      } else if (checkbox.disabled) {
        checkbox.disabled = false;
        checkbox.checked = item.wasChecked !== false;
      }
    }
    el.classList.toggle('is-soldout', !available);

    if (statusEl) {
      const mto = !!(variant && variant.mto);
      statusEl.classList.toggle('is-mto', item.tagMto || mto);
      if (item.tagMto) {
        statusEl.textContent = statusEl.dataset.mtoTag || 'Made to order · 3–7 days';
        statusEl.hidden = false;
      } else if (mto) {
        statusEl.textContent = statusEl.dataset.mto || 'Made to order';
        statusEl.hidden = false;
      } else if (!available) {
        statusEl.textContent = statusEl.dataset.soldout || 'Sold out';
        statusEl.hidden = false;
      } else {
        statusEl.hidden = true;
      }
    }
  }

  // --- footer: selection count + running total ---
  selectedItems() {
    return (this.items || []).filter((i) => {
      const checkbox = i.el.querySelector('[data-vrt-look-check]');
      return checkbox?.checked && i.variant && i.variant.available;
    });
  }

  updateFooter() {
    const selected = this.selectedItems();
    if (this.countEl) this.countEl.textContent = selected.length;
    if (this.addBtn) this.addBtn.disabled = selected.length === 0;

    if (this.totalEl && this.totalWrapEl) {
      try {
        const cents = selected.reduce((sum, i) => sum + (i.variant.cents || 0), 0);
        this.totalEl.textContent = formatMoney(cents, this.dataset.moneyFormat, this.dataset.currency);
        this.totalWrapEl.hidden = false;
      } catch (e) {
        this.totalWrapEl.hidden = true;
      }
    }
  }

  // --- add every selected line in one atomic request ---
  async addSelected() {
    const selected = this.selectedItems();
    if (!selected.length || !this.addBtn) return;
    const original = this.addBtn.textContent;
    this.addBtn.disabled = true;
    this.addBtn.textContent = 'Adding…';
    if (this.errorEl) this.errorEl.hidden = true;

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: selected.map((i) => ({ id: i.variant.id, quantity: 1 })),
          sections: ['vrt-cart-drawer'],
        }),
      });
      const data = await res.json();
      if (!res.ok || data.status) {
        this.showError(data.description || "Couldn't add — please try again.");
        this.addBtn.disabled = false;
        this.addBtn.textContent = original;
        return;
      }
      const cart = document.querySelector('vrt-cart');
      cart?.applySections(data.sections);
      cart?.open();
      this.addBtn.textContent = 'Added ✓';
    } catch (e) {
      this.addBtn.textContent = 'Try again';
    }
    setTimeout(() => {
      this.addBtn.disabled = this.selectedItems().length === 0;
      this.addBtn.textContent = original;
    }, 1400);
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }

  // --- lazy complementary-recommendations fetch (recs mode only) ---
  async load() {
    try {
      const res = await fetch(this.dataset.url);
      if (!res.ok) return this.fail();
      const text = await res.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const freshItems = Array.from(doc.querySelectorAll('[data-vrt-look-item]')).filter(
        (el) => !el.hasAttribute('data-vrt-look-current')
      );
      if (freshItems.length === 0) return this.fail();

      // Ignore the fetched copy's own current-item tile/footer — even though
      // `product` stays bound server-side, that render can't know the
      // shopper's live variant selection since page load. Keep this page's
      // own current tile and footer; just adopt the fetched non-current items.
      this.itemsEl.querySelectorAll('.vrt-look__skeleton').forEach((el) => el.remove());
      freshItems.forEach((el) => this.itemsEl.appendChild(el));

      this.dataset.mode = 'fetched';
      if (this.footerEl) this.footerEl.hidden = false;
      this.initItems();
    } catch (e) {
      this.fail();
    }
  }

  fail() {
    this.hidden = true;
  }
}

if (!customElements.get('vrt-shop-the-look')) {
  customElements.define('vrt-shop-the-look', VrtShopTheLook);
}
